import { ExternalLink, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getDownloadCVUrl } from '@/apis/AICVScannerApis';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getApiErrorMessage } from '@/libs/api/errorHandler';
import { getCvPreviewKind, officeEmbedUrl } from './cvPreviewKind';

const TEXT_PREVIEW_MAX_BYTES = 1_500_000;

export type CvPreviewModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resumeId: string | null;
  fileName?: string | null;
};

export default function CvPreviewModal({
  open,
  onOpenChange,
  resumeId,
  fileName,
}: CvPreviewModalProps) {
  const [phase, setPhase] = useState<'idle' | 'loading-url' | 'loading-text' | 'ready' | 'error'>(
    'idle',
  );
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [viewerSrc, setViewerSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !resumeId) {
      setPhase('idle');
      setFileUrl(null);
      setTextContent(null);
      setViewerSrc(null);
      return;
    }

    const kind = getCvPreviewKind(fileName ?? undefined);
    let cancelled = false;

    const run = async () => {
      setPhase('loading-url');
      setFileUrl(null);
      setTextContent(null);
      setViewerSrc(null);

      try {
        const { download_url: downloadUrl } = await getDownloadCVUrl(resumeId);
        if (cancelled) {
          return;
        }

        setFileUrl(downloadUrl);

        if (kind === 'text') {
          setPhase('loading-text');
          try {
            const res = await fetch(downloadUrl);
            if (!res.ok) {
              throw new Error(`Failed to load file (${res.status})`);
            }
            const buf = await res.arrayBuffer();
            if (buf.byteLength > TEXT_PREVIEW_MAX_BYTES) {
              throw new Error('File is too large to preview as text');
            }
            const text = new TextDecoder().decode(buf);
            if (cancelled) {
              return;
            }
            setTextContent(text);
            setPhase('ready');
            return;
          } catch {
            if (cancelled) {
              return;
            }
            setTextContent(null);
            setViewerSrc(downloadUrl);
            setPhase('ready');
            return;
          }
        }

        if (kind === 'office') {
          setViewerSrc(officeEmbedUrl(downloadUrl));
        } else {
          setViewerSrc(downloadUrl);
        }
        setPhase('ready');
      } catch (e) {
        if (cancelled) {
          return;
        }
        console.error(e);
        setPhase('error');
        toast.error(getApiErrorMessage(e, 'Could not load CV preview'));
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [open, resumeId, fileName]);

  const displayTitle = fileName?.trim() || 'Resume';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton
        className="flex h-[min(90vh,920px)] w-[min(96vw,1200px)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none"
      >
        <DialogHeader className="shrink-0 space-y-0 border-b px-4 py-3 text-left">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:pr-8">
            <DialogTitle className="truncate text-base font-semibold">
              CV preview — {displayTitle}
            </DialogTitle>
            {fileUrl ? (
              <Button
                variant="outline"
                size="sm"
                asChild
                className="shrink-0 self-start sm:self-auto"
              >
                <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={14} /> Open in new tab
                </a>
              </Button>
            ) : null}
          </div>
        </DialogHeader>

        <div className="relative min-h-0 flex-1 bg-muted/40">
          {(phase === 'loading-url' || phase === 'loading-text') && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background/80 text-sm text-muted-foreground">
              <Loader2 className="size-8 animate-spin text-primary" aria-hidden />
              {phase === 'loading-text' ? 'Loading text…' : 'Loading document…'}
            </div>
          )}

          {phase === 'error' && (
            <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
              Preview failed. Use &quot;Open in new tab&quot; if the button appears after retrying,
              or close and try again.
            </div>
          )}

          {phase === 'ready' && textContent !== null && (
            <pre className="max-h-full overflow-auto p-4 text-left text-xs leading-relaxed font-mono whitespace-pre-wrap break-words">
              {textContent}
            </pre>
          )}

          {phase === 'ready' && textContent === null && viewerSrc ? (
            <iframe
              title="CV preview"
              src={viewerSrc}
              className="size-full min-h-[60vh] border-0"
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
